/**
 * GA4 계측 유틸 (FO analytics SSoT) — 마케팅 유입 분석 전용.
 *
 * "어느 채널에서 들어와 어떤 페이지를 보고 전환했는가"를 답하기 위한 계측이다. 제품 사용성 분석
 * (검사 이탈 지점·장바구니 이탈 패턴 등)은 범위 밖 — 그쪽은 자체 DB(`analytics_event`·결제 테이블)로 본다.
 *
 * ## 자체 `POST /fo/events`(api/landing.ts)와의 관계 — 대체가 아니라 병존
 * 자체 적재는 정산·매출 SSoT(정확성 우선)이고, GA4 는 유입 경로 분석(마케팅)이다. 같은 지표를 양쪽에
 * 재구현하면 숫자가 어긋나 신뢰를 잃으므로, 역할을 섞지 않는다. `sessionId`(lib/sessionId.ts)도 GA4 의
 * client_id 와 무관한 별개 식별자다.
 *
 * ## 로더를 index.html 이 아니라 여기서 주입하는 이유
 * 1. dev 미로딩 — index.html 엔 env 분기 문법이 없어 `pnpm dev` 에서도 히트가 찍힌다.
 * 2. SSG 오염 — 빌드가 `vite-ssg` 라 index.html 을 템플릿으로 라우트별 HTML 을 프리렌더한다.
 *    거기 gtag 스크립트와 측정 ID 가 그대로 박힌다.
 * 따라서 `initAnalytics()` 를 main.ts 의 isClient 블록에서만 호출해 브라우저에서 동적 주입한다.
 *
 * ## 실패해도 조용히 — 계측이 서비스를 깨뜨리지 않는다
 * 모든 발화는 3중 가드 + try/catch 로 감싸 예외를 밖으로 내지 않는다(landing.ts postEvent 의
 * fire-and-forget 과 같은 철학). 결제 성공 처리가 계측 때문에 실패하는 일은 없어야 한다.
 */
import type { CheckoutOrder, CheckoutOrderItem } from '#/types';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** GA4 측정 ID. 운영에서만 주입되며, 비어 있으면 계측 전체가 no-op 이 된다. */
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';

/** 금액 동반 이벤트의 통화 — GA4 는 value 단독이면 매출 집계에서 누락한다. */
const CURRENCY = 'KRW';

/** 로더 중복 주입 차단(HMR·중복 호출 대비). */
let loaderInjected = false;

/**
 * 장바구니 계측에 필요한 최소 형태 — `University` 전체에 결합하지 않기 위한 구조적 타입.
 * 담기 진입점이 여러 곳이라 도메인 타입을 그대로 받으면 analytics 가 화면 모델 변경에 끌려다닌다.
 */
export interface CartItemLike {
  /** 대학 식별자 — contentId 가 없을 때 item_id 폴백에 쓴다. */
  universityId: number;
  /** 대학명 — item_name 폴백. */
  name: string;
  /** content_item.id — 자료 미존재 대학은 null. */
  contentId: number | null;
}

/** `trackPurchase` 입력 — 결제 승인 확정 후의 값만 담는다. */
export interface PurchaseParams {
  /** 서버 발번 주문번호(DLX-…). GA4 transaction_id 로 쓰여 중복 제거의 키가 된다. */
  orderNo: string;
  /** PG 가 실제 승인한 금액(원). */
  amount: number;
  /** 구매 자료 목록. 조회 실패 시 빈 배열이어도 purchase 자체는 발화한다. */
  items: CheckoutOrderItem[];
}

/**
 * GA4 이벤트 1건 발화 — 모든 track* 이 경유하는 단일 통로.
 *
 * 3중 가드를 순서대로 통과해야 실제 전송된다.
 *  1. window 부재 — SSG renderToString 패스(Node). 반드시 첫 번째여야 ReferenceError 를 피한다.
 *  2. 측정 ID 부재 — dev.
 *  3. gtag 부재 — 광고 차단기·네트워크 실패.
 */
function track(eventName: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  try {
    if (!window.gtag) return;
    window.gtag('event', eventName, params);
  } catch {
    // 계측 실패는 서비스 흐름에 영향을 주지 않는다 — 조용히 무시.
  }
}

/**
 * 콤마 포함 금액 문자열 → 숫자. `'6,800'` → `6800`.
 * 파싱 불가·null 이면 undefined 를 돌려 호출부가 value 를 생략하게 한다
 * (`resolveFreeOriginalPrice()` 가 표시용 문자열을 반환하므로 여기서 흡수한다).
 */
function parseWon(price: string | null | undefined): number | undefined {
  if (!price) return undefined;
  const n = Number(price.replaceAll(',', ''));
  return Number.isFinite(n) ? n : undefined;
}

/** 장바구니 항목 → GA4 items 1건. contentId 가 없으면 대학 기준으로 식별자를 만든다. */
function toCartGaItem(item: CartItemLike): Record<string, unknown> {
  return {
    item_id: item.contentId === null ? `university_${item.universityId}` : String(item.contentId),
    item_name: item.name,
  };
}

/** 주문 항목 → GA4 items 1건. 자료 제목 우선, 없으면 콘텐츠 식별자로 대체한다. */
function toOrderGaItem(item: CheckoutOrderItem): Record<string, unknown> {
  return {
    item_id: String(item.contentId),
    item_name: item.title ?? `content_${item.contentId}`,
  };
}

/**
 * GA4 로더 주입 + config. main.ts 의 isClient 블록에서 1회 호출한다.
 * 측정 ID 미설정(dev)·SSR·중복 호출은 무해한 no-op.
 *
 * `send_page_view: false` 가 핵심 — 끄지 않으면 로더 자동 발화와 라우터 afterEach 발화가 겹쳐
 * 랜딩 페이지뷰가 항상 2배로 잡힌다. 페이지뷰는 전량 trackPageView 로만 보낸다.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (loaderInjected) return;
  try {
    loaderInjected = true;

    window.dataLayer = window.dataLayer ?? [];
    // gtag 는 반드시 arguments 를 그대로 push 하는 함수여야 한다(배열 스프레드로 바꾸면 GA4 가 인식 못 함).
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.append(script);
  } catch {
    // 주입 실패 시에도 track() 이 gtag 부재로 no-op 이 되므로 별도 복구를 하지 않는다.
  }
}

/**
 * SPA 가상 페이지뷰 1건.
 *
 * @param screen 논리 화면명(route.name, kebab-case) — `/guide/:slug` 처럼 파라미터로 갈라지는 경로를
 *   하나로 묶어 집계하기 위한 축. 빈 값이면 'unknown'.
 * @param path 전체 경로(fullPath) — utm_*·gclid 등 유입 파라미터를 보존해야 하므로 쿼리를 포함한다.
 */
export function trackPageView(screen: string, path: string): void {
  track('page_view', {
    screen_name: screen || 'unknown',
    page_path: path,
    page_title: typeof document === 'undefined' ? '' : document.title,
  });
}

/** 회원가입 완료. 로컬·구글 경로를 method 로 구분한다. */
export function trackSignUp(method: 'google' | 'local'): void {
  track('sign_up', { method });
}

/**
 * 장바구니 담기 — 실제로 담긴 1건만.
 * 가격은 보내지 않는다(장바구니가 금액을 보유하지 않는 설계 — 금액은 서버가 재계산한다).
 */
export function trackAddToCart(item: CartItemLike): void {
  track('add_to_cart', { items: [toCartGaItem(item)] });
}

/** 장바구니 제거 — toggle 대칭. 대상이 없으면 no-op. */
export function trackRemoveFromCart(item: CartItemLike | null | undefined): void {
  if (!item) return;
  track('remove_from_cart', { items: [toCartGaItem(item)] });
}

/** 결제 시작 — 주문 생성으로 금액이 확정된 시점 1회. */
export function trackBeginCheckout(order: CheckoutOrder): void {
  track('begin_checkout', {
    currency: CURRENCY,
    value: order.amount,
    items: (order.items ?? []).map((item) => toOrderGaItem(item)),
  });
}

/**
 * 결제 완료.
 *
 * `transaction_id` 에 서버 발번 orderNo 를 쓰는 것이 중복 방지의 핵심이다 — 성공 URL 에서 새로고침해
 * 재발화하더라도 GA4 가 같은 transaction_id 를 서버 측에서 중복 제거한다. orderId 숫자나 paymentKey 를
 * 쓰면 이 방어가 무너진다.
 */
export function trackPurchase(params: PurchaseParams): void {
  track('purchase', {
    transaction_id: params.orderNo,
    currency: CURRENCY,
    value: params.amount,
    items: params.items.map((item) => toOrderGaItem(item)),
  });
}

/**
 * 무료 이벤트 수령(커스텀 이벤트).
 *
 * 0원 발급이라 `purchase` 로 보내지 않는다 — 매출 리포트가 오염된다. value 에는 정가 상당액을 담아
 * 무료 퍼널의 가치를 따로 볼 수 있게 한다.
 *
 * @param originalPrice 콤마 포함 표시용 금액 문자열(`'6,800'`). 파싱 실패·null 이면 value 를 생략한다.
 */
export function trackClaimFree(items: CartItemLike[], originalPrice: string | null): void {
  const value = parseWon(originalPrice);
  track('claim_free', {
    items: items.map((item) => toCartGaItem(item)),
    ...(value === undefined ? {} : { currency: CURRENCY, value }),
  });
}

/** 샘플 열람(커스텀 이벤트) — 대학명을 함께 남길 수 있는 지점. */
export function trackViewSample(
  universityId: number,
  universityName: string,
  materialId?: number,
): void {
  track('view_sample', {
    university_id: universityId,
    university_name: universityName,
    ...(materialId === undefined ? {} : { material_id: materialId }),
  });
}

/** 상품 발췌본 미리보기 진입 지점 — GA4 `placement` 파라미터 값. */
export type ProductPreviewPlacement = 'product' | 'landing';

/**
 * 상품 발췌본 열람(커스텀 이벤트, #341) — 자체 `INTERVIEW_PREVIEW_VIEW` 적재와 병존한다.
 * 진입 지점(구매 화면·랜딩)을 나눠, 미리보기가 구매 결정 직전에 쓰이는지 탐색 단계에서 쓰이는지 본다.
 */
export function trackViewProductPreview(
  groupCode: string,
  placement: ProductPreviewPlacement,
): void {
  track('view_product_preview', { group_code: groupCode, placement });
}

// ── 성적 분석 비로그인 미리보기(077) — 샘플 뷰 전용 커스텀 이벤트 3종 ─────────
//
// 로그인 여부 구분은 별도 파라미터가 아니라 **이 이벤트들의 존재**로 성립한다 — 샘플 뷰에서만
// 발화하고 회원 뷰(실데이터)에서는 절대 발화하지 않는다(FR-018). 파라미터는 축·행동명만이며
// 성적값·대학명·개인정보를 싣지 않는다(analytics-contract.md).

/** 샘플 뷰 축 — GA4 `axis` 파라미터 값. */
export type SampleAxis = 'jeongsi' | 'susi' | 'tests';

/** 샘플 뷰 조회(커스텀 이벤트) — `useSamplePreview` 가 마운트 시 1회 보낸다. */
export function trackSamplePreviewView(axis: SampleAxis): void {
  track('sample_preview_view', { axis });
}

/** 상단 배너 [로그인하고 내 성적으로 분석하기] 클릭(커스텀 이벤트). */
export function trackSampleLoginCta(axis: SampleAxis): void {
  track('sample_login_cta', { axis });
}

/**
 * 잠금 행동 시도(커스텀 이벤트) — 실데이터 경계 행동에서 로그인 안내 모달이 뜨기 직전.
 * @param action `LockAction` 값 그대로(snake 변환 없음 — 예 `filter`, `start:CAREER`).
 */
export function trackSampleLockAttempt(axis: SampleAxis, action: string): void {
  track('sample_lock_attempt', { axis, action });
}

// ── AI 입시분석 워크스페이스 진입(커머스 → 워크스페이스 교차 전환) ──
// 커머스의 유도 배너(히어로 슬라이드·랜딩 고정 섹션·모바일 메뉴 카드) CTA 클릭. 분석 워크스페이스로 넘어가는
// 첫 전환 지표라 배치 지점(`placement`)만 싣는다 — "배너에 설명을 더 넣을지"는 이 수치로 판단한다.

/** 진입 CTA 배치 지점 — GA4 `placement` 파라미터 값. */
export type AnalysisEntryPlacement = 'hero_slide' | 'landing_section' | 'mobile_menu';

/** AI 워크스페이스 진입 CTA 클릭(커스텀 이벤트) — `AiWorkspaceCta`·`NavAiPromo` 가 보낸다. */
export function trackAnalysisEntry(placement: AnalysisEntryPlacement): void {
  track('analysis_entry_click', { placement });
}

// ── 통합 검색(078) — analytics-contract.md ──
// 질의 문자열·대학명·학과명은 싣지 않는다(검색어는 개인 관심사). 미매칭 질의 원문은 서버 전용 테이블이 담당.

/** 검색 패널 진입 원천 — GA4 `source` 파라미터 값. */
export type SearchOpenSource = 'hero' | 'gnb' | 'shortcut';

/** 검색 결과 그룹 — GA4 `group` 파라미터 값. */
export type SearchResultGroupKey = 'jeongsi' | 'susi' | 'function';

/** 검색 패널 열림(커스텀 이벤트) — 닫힘→열림 전이 시 1회. */
export function trackSearchOpen(source: SearchOpenSource): void {
  track('search_open', { source });
}

/**
 * 검색 결과 클릭(커스텀 이벤트) — `position` 은 전 그룹 통합 순번(0부터).
 * `guest` = 비로그인 클릭(대학·학과 결과는 로그인으로 유도) — 검색발 로그인 전환율의 분모.
 */
export function trackSearchResultClick(
  group: SearchResultGroupKey,
  position: number,
  guest: boolean,
): void {
  track('search_result_click', { group, position, guest });
}

/** 검색 결과 없음(커스텀 이벤트) — 디바운스된 질의 1건당 1회. */
export function trackSearchNoResult(): void {
  track('search_no_result', {});
}

/**
 * 상품군 무료 이벤트 퍼널(081 FR-023) — 안내 진입 → 받기 → 뷰어 → 후기 작성 진입 → 다운로드.
 * 공통 파라미터 `event_group`(상품군 코드)으로 다음 이벤트도 같은 이름을 재사용한다.
 * 050 `claim_free`(장바구니 아이템 배열 계약)와 의미가 달라 재사용하지 않는다.
 */
export function trackFreeEventView(group: string): void {
  track('free_event_view', { event_group: group });
}

export function trackFreeEventClaim(group: string): void {
  track('free_event_claim', { event_group: group });
}

export function trackFreeEventViewerOpen(group: string): void {
  track('free_event_viewer_open', { event_group: group });
}

export function trackFreeEventReviewStart(group: string): void {
  track('free_event_review_start', { event_group: group });
}

export function trackFreeEventDownload(group: string): void {
  track('free_event_download', { event_group: group });
}

/** 무료판 → 유료 상품 화면 이동 위치 — GA4 `placement` 파라미터 값. */
export type FreeEventUpsellPlacement = 'event_page' | 'library';

/** 무료판 받은 뒤 전체 구성(유료 상품 화면)으로 이동 — 무료 이벤트의 유료 전환 퍼널 시작점. */
export function trackFreeEventUpsellClick(
  group: string,
  placement: FreeEventUpsellPlacement,
): void {
  track('free_event_upsell_click', { event_group: group, placement });
}

// ── 입시 소식(083 FR-024) — 검색 유입 성과·#359 착수 판단 근거 ──
// 글 식별자(`news_id`)만 싣는다(제목은 운영 중 바뀔 수 있어 집계 키로 부적합).

/** 가입 유도 클릭 종류 — GA4 `cta` 파라미터 값. */
export type NewsSignupCta = 'signup' | 'login';

/** 입시 소식 목록 조회(커스텀 이벤트) — 목록 화면 마운트 시 1회. */
export function trackNewsListView(): void {
  track('news_list_view', {});
}

/** 입시 소식 상세 조회(커스텀 이벤트) — 글이 표시될 때 id 당 1회. */
export function trackNewsDetailView(newsId: number): void {
  track('news_detail_view', { news_id: newsId });
}

/** 입시 소식 상세의 가입·로그인 유도 클릭(커스텀 이벤트, 비로그인만 노출). */
export function trackNewsSignupCta(newsId: number, cta: NewsSignupCta): void {
  track('news_signup_cta', { news_id: newsId, cta });
}
